import { useState, useRef } from "react";
import { Button } from "./ui/button";
import { Video, Upload, Camera, RotateCcw, Check, X, Loader2 } from "lucide-react";

export default function VideoUploader({
  currentVideoPath,
  onUpload,
  prompts = [],
  title = "Upload Video",
  subtitle = "",
  maxSizeMB = 100,
  maxDurationLabel = "30–90 seconds",
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(!!currentVideoPath);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL + "/api";

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setError("");
    const validTypes = ["video/mp4", "video/quicktime", "video/mov"];
    if (!validTypes.includes(selected.type) && !selected.name.match(/\.(mp4|mov)$/i)) {
      setError("Only MP4 and MOV formats are accepted.");
      return;
    }
    if (selected.size > maxSizeMB * 1024 * 1024) {
      setError(`File too large. Maximum size is ${maxSizeMB}MB.`);
      return;
    }
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
    setUploaded(false);
  };

  const handleUpload = async () => {
    if (!file || !onUpload) return;
    setUploading(true);
    setError("");
    try {
      await onUpload(file);
      setUploaded(true);
      setFile(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed. Please try again.");
    }
    setUploading(false);
  };

  const handleReplace = () => {
    setFile(null);
    setPreview(null);
    setUploaded(false);
    setError("");
    fileRef.current?.click();
  };

  const hasVideo = uploaded || currentVideoPath;

  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] shadow-sm overflow-hidden" data-testid="video-uploader">
      {/* Header */}
      <div className="p-5 pb-3">
        <div className="flex items-center gap-3 mb-1">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${hasVideo ? "bg-[#1a6b3c]/10" : "bg-[#e8633a]/10"}`}>
            <Video className={`w-4.5 h-4.5 ${hasVideo ? "text-[#1a6b3c]" : "text-[#e8633a]"}`} />
          </div>
          <div>
            <p className="font-semibold text-[#0d0d0d] text-sm">{title}</p>
            {subtitle && <p className="text-[11px] text-[#888]">{subtitle}</p>}
          </div>
          {hasVideo && (
            <span className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-[#1a6b3c] bg-[#1a6b3c]/10 px-2.5 py-1 rounded-full">
              <Check className="w-3 h-3" /> Uploaded
            </span>
          )}
        </div>
      </div>

      {/* Prompts */}
      {prompts.length > 0 && !preview && !hasVideo && (
        <div className="px-5 pb-3">
          <p className="text-[11px] font-medium text-[#888] uppercase tracking-wider mb-2">What to include:</p>
          <div className="flex flex-wrap gap-1.5">
            {prompts.map((p) => (
              <span key={p} className="bg-[#faf8f3] text-[#555] text-[11px] px-2.5 py-1 rounded-full border border-black/[0.04]">
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="px-5 pb-3">
          <div className="relative rounded-xl overflow-hidden bg-black">
            <video
              src={preview}
              controls
              className="w-full max-h-[240px] object-contain"
              data-testid="video-preview"
            />
            <button
              onClick={() => { setFile(null); setPreview(null); }}
              className="absolute top-2 right-2 w-7 h-7 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Current video indicator */}
      {hasVideo && !preview && (
        <div className="px-5 pb-3">
          <div className="rounded-xl bg-[#faf8f3] border border-black/[0.04] p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-[#1a6b3c]/10 flex items-center justify-center">
              <Video className="w-5 h-5 text-[#1a6b3c]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-[#0d0d0d]">Video uploaded</p>
              <p className="text-[11px] text-[#888]">Your video is live and visible</p>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-5 pb-3">
          <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="video-error">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="px-5 pb-5">
        <input
          ref={fileRef}
          type="file"
          accept="video/mp4,video/quicktime,.mp4,.mov"
          className="hidden"
          onChange={handleFileSelect}
          data-testid="video-file-input"
        />

        {!preview && !hasVideo ? (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="video-upload-btn"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-black/[0.08] hover:border-[#1a6b3c] transition-colors cursor-pointer"
            >
              <Upload className="w-5 h-5 text-[#888]" />
              <span className="text-xs font-medium text-[#888]">Upload File</span>
              <span className="text-[10px] text-[#aaa]">MP4, MOV · {maxSizeMB}MB max</span>
            </button>
            <button
              onClick={() => {
                // Use file input with capture for mobile camera
                if (fileRef.current) {
                  fileRef.current.setAttribute("capture", "user");
                  fileRef.current.click();
                  fileRef.current.removeAttribute("capture");
                }
              }}
              data-testid="video-record-btn"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-black/[0.08] hover:border-[#1a6b3c] transition-colors cursor-pointer"
            >
              <Camera className="w-5 h-5 text-[#888]" />
              <span className="text-xs font-medium text-[#888]">Record</span>
              <span className="text-[10px] text-[#aaa]">{maxDurationLabel}</span>
            </button>
          </div>
        ) : preview && !uploaded ? (
          <div className="flex gap-2">
            <Button
              onClick={handleUpload}
              disabled={uploading}
              data-testid="video-confirm-upload-btn"
              className="flex-1 h-10 rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white text-sm font-semibold"
            >
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Check className="w-4 h-4 mr-2" /> Upload Video</>}
            </Button>
            <Button
              onClick={() => { setFile(null); setPreview(null); }}
              variant="outline"
              className="h-10 rounded-xl border-black/[0.08] text-sm"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            onClick={handleReplace}
            variant="outline"
            data-testid="video-replace-btn"
            className="w-full h-10 rounded-xl border-black/[0.08] text-sm"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Replace Video
          </Button>
        )}
      </div>
    </div>
  );
}
